import { CibleNewsletter } from './CibleNewsletter.js';
import { Button, IconButton, Input } from '@artosera/ui';
import { ArrowDown, ArrowUp, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { texteEnLangue, type Langue } from '@artosera/shared';
import { useOeuvres } from '../../api/oeuvres.js';
import { useExpositions } from '../../api/expositions.js';
import type { BlocNewsletter } from '../../api/newsletter.js';
import { ObjetCartel, proprietesCartel } from '../../composants/ObjetCartel.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import { useShellContext } from '../shell/context.js';
import styles from './NewsletterPage.module.css';
export function BlocsEditeur({
  blocs,
  onChange,
  lectureSeule,
  langue,
}: {
  blocs: BlocNewsletter[];
  onChange: (v: BlocNewsletter[]) => void;
  lectureSeule: boolean;
  langue: Langue;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug } = useShellContext();
  const [choix, setChoix] = useState<'oeuvre' | 'exposition' | null>(null);
  const [q, setQ] = useState('');
  const oeuvres = useOeuvres(galerieSlug, { statut: ['disponible'], q, vignettes: true });
  const expositions = useExpositions(galerieSlug);
  const changer = (i: number, v: BlocNewsletter) =>
    onChange(blocs.map((b, j) => (i === j ? v : b)));
  const deplacer = (i: number, sens: number) => {
    const copie = [...blocs];
    const autre = copie[i + sens];
    const ici = copie[i];
    if (autre && ici) {
      copie[i] = autre;
      copie[i + sens] = ici;
      onChange(copie);
    }
  };
  return (
    <section className={styles.section} aria-label={t('regie:newsletter.campagne.blocs')}>
      <h2>{t('regie:newsletter.campagne.blocs')}</h2>
      <ol className={styles.blocs}>
        {blocs.map((bloc, i) => {
          const oeuvre =
            bloc.type === 'oeuvre'
              ? oeuvres.data?.oeuvres.find((o) => o.id === bloc.oeuvreId)
              : null;
          const expo =
            bloc.type === 'exposition'
              ? expositions.data?.expositions.find((e) => e.id === bloc.expositionId)
              : null;
          return (
            <li className={styles.carte} key={i}>
              {bloc.type === 'texte' ? (
                <label>
                  {t('regie:newsletter.campagne.texte')}
                  <textarea
                    value={bloc.texte}
                    readOnly={lectureSeule}
                    onChange={(e) => changer(i, { type: 'texte', texte: e.target.value })}
                  />
                </label>
              ) : (
                <>
                  <CibleNewsletter
                    cible={{
                      type: bloc.type,
                      id: bloc.type === 'oeuvre' ? bloc.oeuvreId : bloc.expositionId,
                    }}
                    oeuvre={oeuvre}
                    exposition={expo}
                    url={oeuvre ? oeuvres.data?.vignettes?.urls[oeuvre.id] : null}
                    langue={langue}
                  />
                  <label>
                    {t('regie:newsletter.campagne.lien')}
                    <Input
                      type="url"
                      readOnly={lectureSeule}
                      value={bloc.lien ?? ''}
                      onChange={(e) => changer(i, { ...bloc, lien: e.target.value || null })}
                    />
                  </label>
                </>
              )}
              {!lectureSeule ? (
                <div className={styles.actions}>
                  <IconButton
                    variant="outline"
                    icon={<ArrowUp size={18} />}
                    aria-label={t('regie:newsletter.campagne.monter')}
                    disabled={i === 0}
                    onClick={() => deplacer(i, -1)}
                  />
                  <IconButton
                    variant="outline"
                    icon={<ArrowDown size={18} />}
                    aria-label={t('regie:newsletter.campagne.descendre')}
                    disabled={i === blocs.length - 1}
                    onClick={() => deplacer(i, 1)}
                  />
                  <IconButton
                    variant="outline"
                    icon={<X size={18} />}
                    aria-label={t('regie:newsletter.campagne.retirer')}
                    onClick={() => onChange(blocs.filter((_, j) => j !== i))}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      {!lectureSeule ? (
        <div className={styles.actions}>
          <Button
            variant="secondary"
            onClick={() => onChange([...blocs, { type: 'texte', texte: '' }])}
          >
            {t('regie:newsletter.campagne.ajouterTexte')}
          </Button>
          <Button variant="secondary" onClick={() => setChoix('oeuvre')}>
            {t('regie:newsletter.campagne.ajouterOeuvre')}
          </Button>
          <Button variant="secondary" onClick={() => setChoix('exposition')}>
            {t('regie:newsletter.campagne.ajouterExposition')}
          </Button>
        </div>
      ) : null}
      {choix === 'oeuvre' ? (
        <>
          <label>
            {t('regie:newsletter.campagne.rechercher')}
            <Input value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div
            role="listbox"
            aria-label={t('regie:newsletter.campagne.ajouterOeuvre')}
            className={styles.choix}
          >
            {oeuvres.data?.oeuvres.map((o) => (
              <button
                type="button"
                role="option"
                aria-selected={false}
                className={styles.option}
                key={o.id}
                onClick={() => {
                  onChange([...blocs, { type: 'oeuvre', oeuvreId: o.id }]);
                  setChoix(null);
                }}
              >
                <VignetteOeuvre url={oeuvres.data?.vignettes?.urls[o.id] ?? null} />
                <ObjetCartel {...proprietesCartel(o, langue)} />
              </button>
            ))}
          </div>
        </>
      ) : null}
      {choix === 'exposition' ? (
        <div
          role="listbox"
          aria-label={t('regie:newsletter.campagne.ajouterExposition')}
          className={styles.choix}
        >
          {expositions.data?.expositions.map((e) => (
            <button
              type="button"
              role="option"
              aria-selected={false}
              className={styles.option}
              key={e.id}
              onClick={() => {
                onChange([...blocs, { type: 'exposition', expositionId: e.id }]);
                setChoix(null);
              }}
            >
              <ObjetCartel titre={texteEnLangue(e.titre, langue)} />
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
